import React from "react";
import { format, addDays } from "date-fns";
import { CalendarIcon } from "lucide-react";
import Calendar from "react-calendar";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Base calendar styles (from react-calendar package)
import "react-calendar/dist/Calendar.css";
// shadcn-style overrides
import "@/styles/react-calendar.shadcn.css";

export function DatePickerInput({
  value,
  onChange,
  minDate,
  maxDays,
  disabled = false,
  className,
  title,
  mode = "date", // ✅ NEW
}) {
  const normalizeDate = React.useCallback(
    (input) => {
      if (!input) return null;

      const parsed =
        input instanceof Date
          ? new Date(input)
          : new Date(mode === "month" && typeof input === "string" ? `${input}-01` : input);

      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    [mode],
  );

  const date = normalizeDate(value);
  const resolvedMinDate = normalizeDate(minDate);

  const [open, setOpen] = React.useState(false);

  const maxBaseDate = resolvedMinDate || new Date();

  const resolvedMaxDate =
    typeof maxDays === "number" && Number.isFinite(maxDays)
      ? addDays(maxBaseDate, maxDays)
      : undefined;

  if (disabled) {
    return (
      <div
        title={title}
        className={cn(
          "w-full rounded-md border px-3 py-2 text-sm",
          "bg-gray-100 dark:bg-gray-800 text-muted-foreground cursor-not-allowed",
          className,
        )}
      >
        {date
          ? mode === "month"
            ? format(date, "yyyy-MM")
            : format(date, "yyyy-MM-dd")
          : "—"}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-foreground/70" />
          {date
            ? mode === "month"
              ? format(date, "yyyy-MM")
              : format(date, "yyyy-MM-dd")
            : mode === "month"
              ? "Select month"
              : "Select date"}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[350px] max-w-[90vw] p-2" align="start">
        <Calendar
          value={date}
          minDate={resolvedMinDate || undefined}
          maxDate={resolvedMaxDate}
          view={mode === "month" ? "year" : "month"} // ✅ key change
          maxDetail={mode === "month" ? "year" : "month"} // prevent day drill-down
          onChange={(d) => {
            if (mode === "month") {
              onChange(format(d, "yyyy-MM"));
            } else {
              onChange(format(d, "yyyy-MM-dd"));
            }
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
